import { defineDocument } from '../../schema';
export const gameFeedTheFarmDocument = defineDocument({
    id: 'game:feed-the-farm',
    kind: 'game',
    slug: 'feed-the-farm',
    route: '/discover/games/feed-the-farm',
    seo: {
        title: 'Discover Feed The Farm',
        description: 'A fast free-for-all party game where 2-16 players pick mystery cards to feed their farm animals, dodge chaos and crap-outs, and race to 100 points.',
        imageAsset: 'coverFeedTheFarm',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Feed The Farm',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Players race to 100 points by picking mystery cards to feed their farm animal. Good, Bad, Chaos, and Crap cards have different effects on scores.',
            },
        ],
        imageAsset: 'coverFeedTheFarm',
        imageAlt: 'Feed The Farm game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Welcome to the filthiest farm on the internet. In this rapid-fire game, players choose one of three mystery cards or sit out for the round.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The mystery cards are full of good food to plump your animal and score up, but some delicacies will shrink you back down. Chaos cards cause mayhem. Crap cards completely wipe your score to zero and leave behind a permanent poop monument of shame.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The first of you nasty little animals to reach 100 points wins.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '2-16 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Selection',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~10 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'April 4, 2026',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '69c55319c9763aa207ae0fb2',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Each game uses a 52-card deck comprised of 24 Good cards, 13 Bad cards, 13 Chaos cards, and 2 Crap cards. The 13 Chaos cards are drawn from a larger bank, so the mix of mayhem changes from game to game.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The deck is shared by the whole table and depletes as cards are dealt. A legend on the host screen shows how many of each card type remain, and the deck reshuffles once it runs low.',
                        },
                    ],
                },
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Each round deals three face-down cards.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Everyone picks one of the three mystery feed cards or chooses to sit out and rest. You can switch your pick until either the timer runs out or everyone has made a choice.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'All three cards flip over to reveal whether the card is helpful or hurtful to the players who picked it, or causes mayhem for the group. Everyone who picked the same card gets the same result. Resting in the barn scores +1 that round, unless a Chaos card impacts them somehow.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The three cards are discarded, whether or not anyone picked them, and play continues to the next round.',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The first player to reach 100 or more points with a unique lead wins. If multiple players are tied above 100, play continues until the tie breaks.',
                        },
                    ],
                },
                {
                    type: 'table',
                    variant: 'default',
                    columns: [
                        [
                            {
                                type: 'text',
                                text: 'Card',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Effect',
                            },
                        ],
                    ],
                    rows: [
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Good',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Adds anywhere from 4 to 12 points.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Bad',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Subtracts 1 to 3 points, but your score never drops below 0.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Crap',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Resets your total score to 0 and leaves a permanent poop monument behind.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Chaos',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Shakes things up: adds or multiplies points, forces players to rest or feed, moves points between players, or switches what someone picked.',
                                },
                            ],
                        ],
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'None',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Card names',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: low',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: streamers, immature humor enthusiasts, push-your-luck fans',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/feed-the-farm-comes-back-for-seconds',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Feed The Farm Comes Back For Seconds',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' and the ',
                        },
                        {
                            type: 'link',
                            href: '/blog/feed-the-farm-makes-a-mess-of-push-your-luck',
                            children: [
                                {
                                    type: 'text',
                                    text: 'original blog post',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '69c55319c9763aa207ae0fb2',
                },
            ],
        },
    ],
});
//# sourceMappingURL=feed-the-farm.js.map